import productData from "common/constants/ProductsData";
import CareersIcon from "common/icons/careers_icon.png";
import arrowDown from "common/icons/chevron_down.svg";
import EnneaLogo from "common/icons/Ennea_logo_nav.svg";
import LeadershipIcon from "common/icons/leadership_icon.png";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import BounceAnimation from "../BounceAnimation";

const MainContainer = styled.div`
  display: flex;
  position: fixed;
  background-color: #fff;
  z-index: 1000;
  width: 100%;
`;

const HeaderContainer = styled.div`
  display: flex;
  height: min-content;
  place-content: center;
  border-bottom: 1px solid #e2edf4;
  width: 100%;
`;

const HeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  width: 100%;
`;

const LogoWrapper = styled(Link)`
  place-content: center;
  align-items: center;
  display: flex;
  padding: 1rem 2.81rem;
`;

const Logo = styled.img`
  width: 13.51038rem;
  height: 4.25rem;
`;

const HeaderText = styled.p`
  color: #18191f;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Outfit;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  :hover {
    color: #006eb2;
  }
`;

const DropDownMenuContainer = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 4rem;
  padding: 0 2.81rem;
  position: relative;
  z-index: 199;
`;

const CustomLink = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: center;
  padding: 1rem 0;
  position: relative;
  cursor: pointer;
  z-index: 100;
`;

const Img = styled.img`
  width: 1.0625rem;
  height: 0.625rem;
  flex-shrink: 0;
`;

const DropdownContainer = styled(motion.div)`
  opacity: 0;
  visibility: hidden;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 100;
  background: #fff;
  border: 1px solid #d8d8d8;
  border-radius: 0 0 1.25rem 1.25rem;
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const CustomLinkWrapper = styled.div`
  display: inline-flex;
  position: relative;
  z-index: 100;
  &:hover ${DropdownContainer} {
    opacity: 1;
    visibility: visible;
  }
`;

// Stagger Animation for Menu Items
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

// Products DropDown styles
const ProductDropDownContainer = styled(motion.div)`
  display: inline-flex;
  padding: 1rem;
  align-items: flex-start;
  gap: 5.375rem;
  border-radius: 0rem 0rem 1.25rem 1.25rem;
  background: #fff;
  z-index: 999;
  position: fixed;
  border: 1px solid #d8d8d8;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
`;

const AboutUsMenuContainer = styled(motion.div)`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  z-index: 110;
  justify-content: space-between;
  width: 100%;
  height: 100%;
`;

const AboutUsMenuItemContainer = styled(motion.div)`
  display: flex;
  justify-content: left;
`;

const MenuContainer = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  z-index: 110;
  justify-content: space-between;
`;

const MenuItemContainer = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  :hover {
    background-color: aliceblue;
    border: 0.5px solid grey;
    border-radius: 8px;
  }
`;

const IconContainer = styled.img`
  height: ${(props) => (props.aboutUs ? "25px" : "50px")};
  width: ${(props) => (props.aboutUs ? "25px" : "50px")};
  padding: 5px;
  align-self: center;
`;

const TextContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 5px;
`;

const LabelText = styled(motion.a)`
  margin: 0;
  text-decoration: none;
  color: #006eb2;
  text-align: left;
  font-family: Outfit;
  font-size: 1.275rem;
  font-weight: 600;
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`;

const ProductsDropDown = () => (
  <ProductDropDownContainer
    variants={containerVariants}
    initial="hidden"
    animate="show"
  >
    <MenuContainer>
      {productData.map((data) => (
        <MenuItemContainer
          onClick={() => {
            window.open(data?.website, "_blank");
          }}
        >
          <TextContainer>
            <LabelText variants={itemVariants}>{data?.name}</LabelText>
          </TextContainer>
        </MenuItemContainer>
      ))}
    </MenuContainer>
  </ProductDropDownContainer>
);

// About us Styles
const AboutUsContainer = styled(motion.div)`
  display: inline-flex;
  padding: 2.5rem;
  align-items: flex-start;
  gap: 5.375rem;
  border-radius: 0rem 0rem 1.25rem 1.25rem;
  background: #fff;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
`;

const AboutUsDropDown = () => {
  const navigate = useNavigate();

  return (
    <AboutUsContainer
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      <AboutUsMenuContainer>
        <AboutUsMenuItemContainer>
          <IconContainer
            src={LeadershipIcon}
            alt="Ennea Solutions Leadership"
            aboutUs
          />
          <TextContainer>
            <LabelText
              variants={itemVariants}
              onClick={() => navigate("company/leadership")}
            >
              Leadership
            </LabelText>
          </TextContainer>
        </AboutUsMenuItemContainer>
        <AboutUsMenuItemContainer>
          <IconContainer
            src={CareersIcon}
            alt="Careers in Ennea Solutions"
            aboutUs
          />
          <TextContainer>
            <LabelText
              variants={itemVariants}
              onClick={() => navigate("/company/careers")}
            >
              Careers
            </LabelText>
          </TextContainer>
        </AboutUsMenuItemContainer>
      </AboutUsMenuContainer>
    </AboutUsContainer>
  );
};

const isMobileView = window.innerWidth < 800;

const Header = () => {
  const navigate = useNavigate();

  return isMobileView ? (
    <MobileHeader />
  ) : (
    <MainContainer>
      <HeaderContainer>
        <HeaderWrapper>
          <LogoWrapper to="/">
            <BounceAnimation scale={1.04}>
              <Logo src={EnneaLogo} alt="Ennea Solutions Logo" />
            </BounceAnimation>
          </LogoWrapper>
          <DropDownMenuContainer>
            <CustomLinkWrapper onClick={() => navigate("/")}>
              <CustomLink>
                <HeaderText>Home</HeaderText>
              </CustomLink>
            </CustomLinkWrapper>
            <CustomLinkWrapper>
              <BounceAnimation scale={1.02}>
                <CustomLink>
                  <HeaderText>Products</HeaderText>
                  <Img src={arrowDown} alt="Products Dropdown" />
                </CustomLink>
              </BounceAnimation>
              <DropdownContainer
                initial={{ opacity: 0, y: "32px", x: "-18%" }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                transition={{ duration: 1.2, ease: "easeIn" }}
              >
                <ProductsDropDown />
              </DropdownContainer>
            </CustomLinkWrapper>
            <CustomLinkWrapper onClick={() => navigate("/company/blogs")}>
              <CustomLink>
                <HeaderText>Blogs</HeaderText>
              </CustomLink>
            </CustomLinkWrapper>
            <CustomLinkWrapper onClick={() => navigate("/company/contact-us")}>
              <CustomLink>
                <HeaderText>Contact Us</HeaderText>
              </CustomLink>
            </CustomLinkWrapper>
            <CustomLinkWrapper>
              <BounceAnimation scale={1.02}>
                <CustomLink>
                  <HeaderText>Who We Are</HeaderText>
                  <Img
                    src={arrowDown}
                    alt="Ennea Solutions Leadership and Careers Dropdown"
                  />
                </CustomLink>
              </BounceAnimation>
              <DropdownContainer
                initial={{ opacity: 0, y: "32px", x: "-18%" }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                transition={{ duration: 1.2, ease: "easeIn" }}
              >
                <AboutUsDropDown />
              </DropdownContainer>
            </CustomLinkWrapper>
          </DropDownMenuContainer>
        </HeaderWrapper>
      </HeaderContainer>
    </MainContainer>
  );
};

const MobileHeaderContainer = styled.div`
  display: flex;
  height: min-content;
  place-content: center;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem;
`;
const MobileHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2edf4;
  width: 100%;
`;

const MobileHeader = () => (
  <MobileHeaderContainer>
    <MobileHeaderWrapper>
      <LogoWrapper to="/">
        <BounceAnimation scale={1.04}>
          <Logo src={EnneaLogo} alt="Ennea Solutions Logo" />
        </BounceAnimation>
      </LogoWrapper>
    </MobileHeaderWrapper>
  </MobileHeaderContainer>
);

export default Header;
